import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Breadcrumb from "@/components/Breadcrumb";
import {
  useGetEntities,
  useGetLogActivities,
  useGetLogStatic,
  useGetResourceEntity,
} from "@/hooks/logActivityHooks";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { useEffect, useMemo, useState } from "react";
import type { LogActivityParams } from "@/api/logActivityService";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import LogActivityTable from "./LogActivityTable";
import { DefaultPrimaryButton } from "@/styles/globalstyles";
import { useQueryInvalidation } from "@/lib/queryInvalidation";
import LogDetailModal from "@/components/log-activity/LogDetailModal";

const LogActivity = () => {
  const queryInvalidation = useQueryInvalidation();
  const [filterParams, setFilterParams] = useState<LogActivityParams>({
    search_date: new Date()?.toISOString().split("T")[0],
    only_system: false,
  });
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [selectedResource, setSelectedResource] = useState<string>("");
  const [selectedEntity, setSelectedEntity] = useState<string>("");
  const [onlySystem, setOnlySystem] = useState<boolean | undefined>(false);
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [performedBy, setPerformedBy] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowPerPage, setRowPerPage] = useState(10);
  const [selectedLogId, setSelectedLogId] = useState<number | undefined>();
  const [openDetailModal, setOpenDetailModal] = useState(false);

  const {
    data: logActivities,
    isFetching: isLoadingLogActivities,
    isError: isErrorLogActivities,
  } = useGetLogActivities({
    ...filterParams,
    skip: page * rowPerPage,
    limit: rowPerPage,
  });

  const {
    data: resourceEntities,
    isFetching: isLoadingResourceEntities,
    isError: isErrorResourceEntities,
  } = useGetResourceEntity();

  const {
    data: entities,
    isFetching: isLoadingEntities,
    isError: isErrorEntities,
  } = useGetEntities();

  const {
    data: logStatic,
    isFetching: isLoadingLogStatic,
    isError: isErrorLogStatic,
  } = useGetLogStatic(selectedDate?.toISOString().split("T")[0] || "");

  useQueryHandler({
    isError:
      isErrorLogActivities ||
      isErrorResourceEntities ||
      isErrorEntities ||
      isErrorLogStatic,
  });

  useEffect(() => {
    document.title = "Log Activity | AMPA";
  }, []);

  useEffect(() => {
    queryInvalidation.invalidateLogActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams, page, rowPerPage]);

  const entityOptions = useMemo(() => {
    if (!entities) return [];
    if (!selectedResource) return entities;
    return entities.filter(
      (entity) => entity.resource === selectedResource,
    );
  }, [entities, selectedResource]);

  const onSearch = () => {
    setPage(0);
    setFilterParams({
      ...filterParams,
      search_date: selectedDate?.toISOString().split("T")[0],
      resource: selectedResource || undefined,
      entity: selectedEntity || undefined,
      only_system: onlySystem,
      performed_by: performedBy || undefined,
      action: selectedAction || undefined,
    });
  };

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Breadcrumb current="log" />
      <Box
        display="flex"
        flexDirection="row"
        gap={1}
        sx={{ pt: 2, width: "100%" }}
      >
        <Box flexGrow={1}>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              backgroundColor: "#fafafa",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
              display: "flex",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <DateCalendar
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
              }}
            />
          </Box>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              p: 2,
              backgroundColor: "#fafafa",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "flex-start",
                }}
              >
                <Typography sx={{ fontWeight: 500 }}>วันที่:</Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  {selectedDate ? selectedDate.format("DD/MM/YYYY") : "ไม่ระบุ"}
                </Typography>
              </Box>
              <Box
                sx={{
                  borderTop: "1px solid #e0e0e0",
                  pt: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  position: "relative",
                }}
              >
                {isLoadingLogStatic && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(250, 250, 250, 0.8)",
                      borderRadius: 1,
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      color: "primary.main",
                    }}
                  >
                    Activity ทั้งหมด:
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: "primary.main" }}>
                    {logStatic?.all_log_count || 0} รายการ
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      color: "info.main",
                    }}
                  >
                    Login:
                  </Typography>
                  <Typography sx={{ fontWeight: 500, color: "info.main" }}>
                    {logStatic?.login_log_count || 0} รายการ
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      color: "success.main",
                    }}
                  >
                    Create:
                  </Typography>
                  <Typography sx={{ fontWeight: 500, color: "success.main" }}>
                    {logStatic?.create_log_count || 0} รายการ
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      color: "warning.main",
                    }}
                  >
                    Update:
                  </Typography>
                  <Typography sx={{ fontWeight: 500, color: "warning.main" }}>
                    {logStatic?.update_log_count || 0} รายการ
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      color: "error.main",
                    }}
                  >
                    Delete:
                  </Typography>
                  <Typography sx={{ fontWeight: 500, color: "error.main" }}>
                    {logStatic?.delete_log_count || 0} รายการ
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  borderTop: "1px solid #e0e0e0",
                  pt: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontSize: "0.9rem" }}>
                  จำนวน User ใช้งาน:
                </Typography>
                <Typography sx={{ fontWeight: 600, color: "primary.main" }}>
                  {logStatic?.unique_user_count || 0} คน
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          flexGrow={4}
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            backgroundColor: "#fafafa",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: 1,
                flexGrow: 1,
              }}
            >
              <FormControl size="small" sx={{ width: "17%" }}>
                <InputLabel
                  id="resource-filter-label"
                  sx={{
                    fontSize: "0.8rem",
                    color: "#666666",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  }}
                >
                  หมวดหมู่
                </InputLabel>
                <Select
                  labelId="resource-filter-label"
                  value={selectedResource || ""}
                  onChange={(e) => {
                    setSelectedResource(e.target.value);
                  }}
                  label="หมวดหมู่"
                >
                  {isLoadingResourceEntities ? (
                    <MenuItem disabled>กำลังโหลด...</MenuItem>
                  ) : (
                    [
                      <MenuItem key="all" value="">
                        <Typography>ทั้งหมด</Typography>
                      </MenuItem>,
                      ...(resourceEntities?.map((resourceEntity) => (
                        <MenuItem
                          key={resourceEntity.resource}
                          value={resourceEntity.resource}
                        >
                          <Typography>{resourceEntity.display_name}</Typography>
                        </MenuItem>
                      )) || []),
                    ]
                  )}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ width: "17%" }}>
                <InputLabel
                  id="entity-filter-label"
                  sx={{
                    fontSize: "0.8rem",
                    color: "#666666",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  }}
                >
                  หมวดหมู่ย่อย
                </InputLabel>
                <Select
                  labelId="entity-filter-label"
                  value={selectedEntity || ""}
                  onChange={(e) => {
                    setSelectedEntity(e.target.value);
                  }}
                  label="หมวดหมู่ย่อย"
                >
                  {isLoadingEntities ? (
                    <MenuItem disabled>กำลังโหลด...</MenuItem>
                  ) : (
                    [
                      <MenuItem key="all" value="">
                        <Typography>ทั้งหมด</Typography>
                      </MenuItem>,
                      ...(entityOptions?.map((entityOption) => (
                        <MenuItem
                          key={entityOption.entity}
                          value={entityOption.entity}
                        >
                          <Typography>{entityOption.entity}</Typography>
                        </MenuItem>
                      )) || []),
                    ]
                  )}
                </Select>
              </FormControl>
              <TextField
                label="รหัสพนักงาน"
                variant="outlined"
                size="small"
                value={performedBy || ""}
                onChange={(e) => setPerformedBy(e.target.value)}
                sx={{
                  width: "17%",
                  "& .MuiInputLabel-root": {
                    fontSize: "0.8rem",
                    color: "#666666",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#cccccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#999999",
                    },
                  },
                }}
              />
              <FormControl size="small" sx={{ width: "17%" }}>
                <InputLabel
                  id="log-type-filter-label"
                  sx={{
                    fontSize: "0.8rem",
                    color: "#666666",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  }}
                >
                  ประเภท Log
                </InputLabel>
                <Select
                  labelId="log-type-filter-label"
                  value={onlySystem === undefined ? "" : onlySystem.toString()}
                  onChange={(e) =>
                    setOnlySystem(
                      e.target.value === "true"
                        ? true
                        : e.target.value === "false"
                          ? false
                          : undefined,
                    )
                  }
                  label="ประเภท Log"
                >
                  {[
                    <MenuItem key="all" value="">
                      <Typography>ทั้งหมด</Typography>
                    </MenuItem>,
                    <MenuItem key="user" value="false">
                      <Typography>ผู้ใช้ (User)</Typography>
                    </MenuItem>,
                    <MenuItem key="system" value="true">
                      <Typography>ระบบ (System)</Typography>
                    </MenuItem>,
                  ]}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ width: "17%" }}>
                <InputLabel
                  id="log-action-filter-label"
                  sx={{
                    fontSize: "0.8rem",
                    color: "#666666",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  }}
                >
                  Action
                </InputLabel>
                <Select
                  labelId="log-type-filter-label"
                  value={selectedAction}
                  onChange={(e) =>
                    setSelectedAction(e.target.value)
                  }
                  label="Action"
                >
                  {[
                    <MenuItem key="all" value="">
                      <Typography>ทั้งหมด</Typography>
                    </MenuItem>,
                    <MenuItem key="user" value="login">
                      <Typography>Login</Typography>
                    </MenuItem>,
                    <MenuItem key="system" value="create">
                      <Typography>Create</Typography>
                    </MenuItem>,
                    <MenuItem key="system" value="update">
                      <Typography>Update</Typography>
                    </MenuItem>,
                    <MenuItem key="system" value="delete">
                      <Typography>Delete</Typography>
                    </MenuItem>,
                  ]}
                </Select>
              </FormControl>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Button
                sx={{ ...DefaultPrimaryButton }}
                size="small"
                onClick={onSearch}
              >
                ค้นหา
              </Button>
            </Box>
          </Box>
          <LogActivityTable
            logActivities={logActivities?.data || []}
            count={logActivities?.total || 0}
            isLoading={isLoadingLogActivities}
            page={page}
            rowPerPage={rowPerPage}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
            onRowPerPageChange={(newRowPerPage) => {
              setRowPerPage(newRowPerPage);
              setPage(0);
            }}
            handleOpenDetail={(logActivity) => {
              setSelectedLogId(logActivity.id);
              setOpenDetailModal(true);
            }}
          />
        </Box>
      </Box>

      <LogDetailModal
        open={openDetailModal}
        onClose={() => setOpenDetailModal(false)}
        selectedId={selectedLogId}
      />
    </Box>
  );
};

export default LogActivity;
